import {
  NetworkMembership,
  NetworkVisibility,
  RoleType,
} from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'

export const PermissionsSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()
  const { completeStep } = useAdminOnboarding()

  const isPrivateNetwork = network?.visibility === NetworkVisibility.private
  const isInviteOnly = network?.membership === NetworkMembership.inviteOnly
  const canNonAdminsInvite = network?.whoCanInviteIds?.length !== 0

  return (
    <Card id="permissions">
      <Card.Header
        title={$t({
          defaultMessage: 'Permissions',
          id: 'Admin.PermissionSettings',
        })}
      />
      <Card.Content>
        <Form
          defaultValues={{
            isPrivateNetwork,
            isInviteOnly,
            canNonAdminsInvite,
          }}
          onSubmit={async data => {
            const { isPrivateNetwork, isInviteOnly, canNonAdminsInvite } = data
            completeStep({
              step: 'permission-and-privacy',
            })
            return updateNetwork({
              input: {
                visibility: isPrivateNetwork
                  ? NetworkVisibility.private
                  : NetworkVisibility.public,
                membership: isInviteOnly
                  ? NetworkMembership.inviteOnly
                  : NetworkMembership.open,
                whoCanInviteIds: canNonAdminsInvite
                  ? network?.roles
                      ?.filter?.(role =>
                        role?.type
                          ? [RoleType.member, RoleType.moderator].indexOf(
                              role.type,
                            ) > -1
                          : false,
                      )
                      .map(role => role.id)
                  : [],
              },
            })
          }}
        >
          <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
            <Form.Toggle
              name="isPrivateNetwork"
              label={$t({
                defaultMessage: 'Private community',
                id: 'Admin.PermissionSettings.Private',
              })}
              helperText={$t({
                defaultMessage:
                  'Allow only community members to view and browse the community.',
                id: 'Admin.PermissionSettings.Private.HelperText',
              })}
            />
            <Form.Toggle
              name="isInviteOnly"
              label={$t({
                defaultMessage: 'Invite-only',
                id: 'Admin.PermissionSettings.InviteOnly',
              })}
              helperText={$t({
                defaultMessage:
                  'Allow only people with an invite to join as community members.',
                id: 'Admin.PermissionSettings.InviteOnly.Description',
              })}
            />
            <Form.Toggle
              name="canNonAdminsInvite"
              label={$t({
                defaultMessage: 'Anyone can invite',
                id: 'Admin.PermissionSettings.Public',
              })}
              helperText={$t({
                defaultMessage:
                  'Allow non-admin members to invite others to the community.',
                id: 'Admin.PermissionSettings.Public.Description',
              })}
            />
            <Form.Actions>
              <Button type="submit" loading={isLoading}>
                <T defaultMessage="Update" id="Generics.Update" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
